import create, { GetState, SetState } from 'zustand';
import produce, { castDraft } from 'immer';
import { WritableDraft } from 'immer/dist/types/types-external';
import { useCallback, useMemo } from 'react';
import { IPortalSearchBoxProps } from 'domains/portal/Header/PortalHeader/PortalSearchBox';
import { ISearchResultClickEvent } from 'domains/portal/Header/PortalHeader/SearchResultItem';
import { ActionHeader, EntityActionType } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions';
import { ChromeSetting } from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/ChromeSetting';
import { ISplitterDelta } from 'components/layout/Splitter';
import { Account } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Accounts';
import { getCurrentAccount } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Accounts/AccountService';
import { EntityIdentifier } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder';
import { registerForEventAndSubscribe, syncClientsEventAndFoldersListToServer } from 'utils/ClientEventUtils';
import {
  PortalLoadModel,
  PortalOptions,
  PortalSideBarDefaultPanel,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Portal';
import { fetchWithParameters } from 'utils/ApiUtils';
import { createEvent, executeScriptElements } from 'utils/DomUtils';
import {
  getActivePageName,
  getCurrentFolderDefaultPageName,
  getCurrentFolderId,
  useFolderStore as FolderStore,
} from './FolderStore';
import { getFavoritesState, createFavoriteActions } from './favorites/PortalFavoritesStore';
import { getChromeSettingFromString } from 'utils/ChromeParameterUtils';
import { serviceCatalogEventId } from 'domains/portal/LeftPanel/WorkflowCatalog/WorkflowCatalogUtils';
import { getTreeStore } from 'components/Tree/CommonTree/useTreeStore';
import { workflowCatalogTreeId } from './LeftPanel/WorkflowCatalog/WorkflowCatalog';
import { executeActionByClientActionId, runBaseAction } from 'utils/ActionUtils';
import { getRootWorkflowCatalogItems, IWorkflowCatalogNode } from './LeftPanel/WorkflowCatalog/WorkflowCatalogUtils';
import { createHelpCenterGlobalActions, getHelpCenterState } from 'components/HelpCenter/HelpCenterStore';
import { ServiceFetcher } from '@decisions/server/ServiceFetcher';
import { createFoldersStoreActions, createFolderTreesStores } from './LeftPanel/FolderTree/useFolderTreesStore';
import { QueueUtil } from 'utils/QueueUtils';
import { SystemFolderID } from './LeftPanel/FolderTree/FolderTreeUtils';
import {
  getCollapsedTreeOnStartStudio,
  getCollapsedTreeOnStartUserPortal,
  getCurrentUserEmail,
  getDefaultActiveTab,
  getDefaultFolderId,
  getFrameStyle,
  getSearchPageSize,
  getShowSidebar,
  useSettingsStore as SettingsStore,
} from './SettingsStore';
import {
  history,
  getUpdatedSearchParams,
  getLocationPartsFromUrl,
  getNavigationParams,
  getBasePath,
  getAdditionalParameters,
  evaluateNavParams,
  showFolderInLayer,
  closeFolderLayer,
  InitialUrlParams,
} from 'components/Router';
import { getGlobalLoadingState, usePortalLoadingStore as LoadingStore } from './PortalLoadingStore';
import { updateRuntimeFilterSubscriptions } from 'domains/ReportViewer/utils/ReportUtils';
import { useGlobalActionsStore } from 'domains/stores/GlobalActionsStore';
import { setPortalStoreConnector } from './PortalStoreConnector';

const PORTAL_BREAKPOINT_TABLET = 991;
const DEFAULT_SIDEBAR_WIDTH = 300;
export const FOLDER_CHANGED_EVENT_ID = 'folderChangedEvent';

// MVC action handler for now:
declare var SystemInformation: () => void;
declare var switchPortal: (url: string, isStudio: boolean) => void;
declare var openPortal: () => void;
// this Userback global is pretty gross, but we can at least isolate it here and remove as needed.
// even Userback is a confusing name for "submit feedback"
interface UserbackStatic {
  access_token?: string;
  widget_settings?: object;
  open?: () => void;
}
declare var Userback: UserbackStatic;

/**
 * Describes state / settings for the portal as a whole. This is portal settings information,
 * but also other pieces of global state.
 */
export interface IPortalState
  extends Omit<PortalOptions, '__type'>,
    ReturnType<typeof getFavoritesState>,
    ReturnType<typeof getHelpCenterState>,
    ReturnType<typeof createFolderTreesStores> {
  queue: QueueUtil;
  accountInfo: {
    isImpersonating: boolean;
    account?: Account;
    accountImpersonations: EntityIdentifier[];
    isUserInDesignersOrAdministratorGroup: boolean;
    isAdministratorUser: boolean;
    canSubmitFeedback: boolean;
  };
  isInitialized?: boolean;
  areFoldersSet?: boolean;
  arePageContentsEmpty?: boolean;
  logoUrl?: string;
  search: IPortalSearchBoxProps;
  header: {
    showCurrentFolder?: boolean;
    currentFolderName?: string;
    showNotifications?: boolean;
  };
  sideBar: {
    isOpen?: boolean;
    activeTab: string;
    prevNavigableTab: string;
    width: number;
    sidebarContentSelector: string;
  };
  pageContentSize: {
    width: number;
    height: number;
  };
  // pageStores: Map<string, Page
  navigation: {
    showingSystemInformation: boolean;
    folderId: string;
    /**
     * This could become JSON in the future, but for now it's just HTML
     */
    currentPageData: string;
    pageHost: HTMLElement | null;
    navigationParams: string;
    sidebarTabs: { [key: string]: { activeFolder: string } };
    portalBasePath: string;
  };

  width: number;
  height: number;
  // userContext?: SessionUserContext; // userContext state currently lives in the ServiceFetcher.
  portalContentScroll: { x: number; y: number };
  workflowCatalogRootNodes: IWorkflowCatalogNode[];
}

//#region store creation

type PortalActions = {
  actions: ReturnType<typeof createPortalStoreActions> &
    ReturnType<typeof createFavoriteActions> &
    ReturnType<typeof createHelpCenterGlobalActions> &
    ReturnType<typeof createFoldersStoreActions>;
};

type PortalAccountActions = {
  accountActions: ReturnType<typeof createPortalAccountActions>;
};

export type PortalStore = IPortalState & PortalActions & PortalAccountActions;

/**
 * This is singleton data store per browser tab.
 */
export const usePortalStore = create<PortalStore>((set, get) => ({
  //#region optional properties before model consolidation
  baseUrl: '',
  hasStudioRights: false,
  isAdministratorUser: false,
  isDebuggingEnabled: false,
  isUserInDesignersOrAdministratorGroup: false,
  messageToShow: '',
  returnUrl: '',
  runStartupInstruction: false,
  systemFolderVisible: false,
  isMaintenanceModeEnabled: false,
  //#endregion optional properties before model consolidation
  queue: new QueueUtil(),
  accountInfo: {
    isImpersonating: false,
    accountImpersonations: [],
    isUserInDesignersOrAdministratorGroup: false,
    isAdministratorUser: false,
    canSubmitFeedback: true,
  },
  foldersPanelTitle: 'Folders',
  systemFoldersPanelTitle: 'System',
  recentFoldersPanelTitle: 'Recent',
  favoritesPanelTitle: 'Favorites',
  isStudio: false, // presume not until we get the setup passed in.
  pageActionsPinned: true,
  pageActionsVisible: true,
  workflowCatalogPanelTitle: 'Workflows',
  header: {
    showNotifications: true,
  },
  sideBar: {
    // isOpen: true, // if it's not defined, the frameStyle/chrome setting defines it.
    activeTab: '',
    prevNavigableTab: '',
    width: DEFAULT_SIDEBAR_WIDTH,
    sidebarContentSelector: '.dp-portal-sidebar__contents-body',
  },
  pageContentSize: {
    width: 1560, // this is the size at 1080p. Nothing is currently subscribed to this, but we need a default value.
    height: 492,
  },
  search: {
    pageSize: 5,
    isCollapsed: false,
    runBaseAction: runBaseAction,
    showContextMenu: (event: ISearchResultClickEvent & { actions: ActionHeader[] }) => {
      set(
        produce<PortalStore>(state => {
          state.search.searchContextActions = event.actions;
        })
      );
    },
    hideContextMenu: () => {
      set(
        produce<PortalStore>(state => {
          delete state.search.searchContextActions;
        })
      );
    },
  },
  navigation: {
    showingSystemInformation: false,
    currentPageData: '',
    folderId: InitialUrlParams.FolderId || '',
    pageHost: null,
    navigationParams: '',
    sidebarTabs: {
      Folders: { activeFolder: '' },
      System: { activeFolder: '' },
    },
    portalBasePath: '',
  },
  portalActionsOpen: false,
  portalContentScroll: { x: 0, y: 0 },
  workflowCatalogRootNodes: [],
  width: document?.body?.offsetWidth,
  height: document?.body?.offsetHeight,
  ...getFavoritesState(),
  ...getHelpCenterState(),
  ...createFolderTreesStores(),
  // keep actions stuff at the bottom in case some actions depend on default state being defined.
  actions: createPortalStoreActions(get, set),
  accountActions: createPortalAccountActions(get, set),
  showGlobalDebuggerIndicator: false,
}));

function createPortalStoreActions(get: GetState<PortalStore>, set: SetState<PortalStore>) {
  return {
    init: async (loadModel: PortalLoadModel) => {
      const {
        isAdministratorUser,
        isUserInDesignersOrAdministratorGroup,
        systemFoldersPanelTitle,
        foldersPanelTitle,
      } = loadModel.Options;
      let state = get();
      // fire and forget loading some settings:
      SettingsStore.getState().actions.initializeSettings(loadModel.Options);

      /* leaving handling of these in MVC for now:
      // messageToShow?: string;
      // runStartupInstruction?: boolean;
      // is browser supported warning
      // initializePage();
      // initializeFileUpload(); // leave in MVC for now
      // attachEvents(); - which is just listenPortalResize, could handle here and push out elsewhere via subscription to the store
      // resize();
      onPortalInitialize
      // initlializeUserBack();
      */
      state.actions.initializeFavorites();
      state.actions.initializeWorkflowCatalog();
      state.actions.subscribeToGlobalDebugging();

      // showBrowseView?: boolean; // is this even still a feature?
      // translatedBrowsePageName?: string;

      useGlobalActionsStore.getState().actions.loadGlobalActions(EntityActionType.Silverlight, loadModel.GlobalActions);

      state = get();

      // moved `getDefaultActiveTabName` selector default via settings logic to `PortalLoadModel.ActiveTabName`,
      // so it should always be correct when we initialize it get here:
      const activeTab = loadModel.ActiveTabName;
      const activeFolder = loadModel.CurrentFolderView.FolderID;

      // apply settings from loadModel / URL
      SettingsStore.getState().actions.setPortalSettingInfo(
        loadModel.PortalSettings,
        getChromeSettingFromString(InitialUrlParams.Chrome || InitialUrlParams.chrome),
        loadModel.Options.isStudio
      );

      // set connector for things that shouldn't refer to this directly, because of code-splitting
      setPortalStoreConnector({
        showEmptyPage: state.actions.showEmptyPage,
        onPageAreaSizeChange: state.actions.onPageAreaSizeChange,
        updatePageName: state.actions.updatePageName,
        isFolderTreeInitialized: () => getShowSidebar(SettingsStore.getState()),
      });

      // apply portal options:
      set({
        ...state,
        ...loadModel.Options,
        sideBar: {
          ...state.sideBar,
          activeTab,
        },
        navigation: {
          ...state.navigation,
          sidebarTabs: {
            ...state.navigation.sidebarTabs,
            // set SystemFolderID as default active for System tab.
            [systemFoldersPanelTitle]: { activeFolder: SystemFolderID },
            // set active Folder for whichever tab is active
            [activeTab || foldersPanelTitle]: { activeFolder },
          },
        },
        isInitialized: false, // wait for first navigation to flip this, or some default sidebar settings won't work
        accountInfo: {
          ...state.accountInfo,
          account: loadModel.Account,
          accountImpersonations: loadModel.AccountsCanImpersonate,
          isImpersonating: loadModel.IsImpersonating,
          isAdministratorUser: !!isAdministratorUser,
          isUserInDesignersOrAdministratorGroup: !!isUserInDesignersOrAdministratorGroup,
        },
      });

      state.actions.initializeFolderTrees(loadModel);
      FolderStore.getState().folderStoreActions.updateFolderViewData(
        loadModel.CurrentFolderView,
        loadModel.CurrentPageName
      );

      !IS_TEST && updateRuntimeFilterSubscriptions(activeFolder);
      state.actions.initializeHistory(loadModel);
    },
    async initializeFolderTrees(loadModel: PortalLoadModel) {
      const state = get();
      if (!getShowSidebar(SettingsStore.getState())) {
        return;
      }
      // When we add arbitrary sidebar tabs, we'll need a better abstraction here,
      // but for now we can hard code Folders and System
      const folderId = loadModel.CurrentFolderView.FolderID;
      const folderPromise = state.actions.createFoldersStore(loadModel.FoldersForTabs, folderId);
      const perTabFolderTreePromises = [folderPromise];
      // won't need to await recent, because they are not navigation-blocking
      state.actions.createRecentFoldersStore(loadModel.FoldersForTabs, folderId);
      // if the user has no permissions to see the system tree, don't bother creating it.
      if (getSystemFolderVisible(state) && getIsAdminUser(state)) {
        perTabFolderTreePromises.push(state.actions.createSystemFoldersStore(loadModel.FoldersForTabs, folderId));
      }

      // Wait for all the folder trees to load. (They should all be in `FoldersForTabs`, so the "wait" should be theoretical.)
      await Promise.all(perTabFolderTreePromises);

      set(
        produce<PortalStore>(state => {
          state.areFoldersSet = true;
        })
      );
    },
    async initializeWorkflowCatalog() {
      registerForEventAndSubscribe(serviceCatalogEventId, 'PortalStore', () => {
        const store = getTreeStore(workflowCatalogTreeId);
        if (store) {
          getTreeStore(workflowCatalogTreeId)?.actions.loadTree();
        } else {
          setupRootNodes();
        }
      });

      const setupRootNodes = async () => {
        const nodes = await getRootWorkflowCatalogItems();
        set(
          produce<PortalStore>(state => {
            state.workflowCatalogRootNodes = castDraft(nodes);
          })
        );
      };

      setupRootNodes();
    },
    subscribeToGlobalDebugging() {
      registerForEventAndSubscribe('global_debugger_running', 'global_debugger_running', (event: CustomEvent) => {
        set(
          produce<PortalStore>(state => {
            state.showGlobalDebuggerIndicator = state.isStudio && event.detail.IsRunning;
          })
        );
      });
    },

    setPageContentElement: (pageHost: HTMLElement | null) => {
      const { navigation } = get();
      if (!pageHost || pageHost === navigation.pageHost) {
        return;
      }

      if (navigation.currentPageData) {
        // if we have the data loaded prior to element rendering, render it now.
        renderPageContents(pageHost, navigation.currentPageData);
      }

      // we'll clear the page data so it's just HTML in the DOM not hanging out in the data-store
      set({
        navigation: { ...navigation, pageHost, currentPageData: '' },
      });
    },

    /**
     * This action is about setting which tab is active, based on navigation that has already in process.
     * i.e. Something triggers selection of a folder in a folder tree
     *    - e.g. Initial load of portal, or creation of a new folder
     *    - we are loading the folders, and the tab is activated via the folder tree logic, because that logic knows which tree the folder is in.
     * @param tabName to assign
     * @param folderId folder id
     */
    setActiveSidebarTab: (tabName: string, folderId?: string) => {
      // We still need this until tabName is in the URL, to trigger back and forth from System during navigation.
      // we need to avoid it during very initial load, though.
      const store = get();
      // Verify tab CAN be activated, first:
      if (tabName === getSystemFoldersPanelTitle(store) && !getSystemFolderVisible(store)) {
        return; // don't activate system if they can't see it.
      }

      set(
        produce<PortalStore>(state => {
          // immer.produce to assign to nested properties immutably, easily. Will freeze objects.
          state.sideBar.activeTab = tabName;

          // folder ID needs to be set here only when the folder was not in the tree during onFolderNavigation,
          // e.g. navigation to a nested folder that was not yet in the folder tree.
          if (folderId) {
            state.navigation.sidebarTabs[tabName] = {
              ...state.navigation.sidebarTabs[tabName],
              activeFolder: folderId,
            };
          }
        })
      );
    },

    /**
     * This action is about triggering navigation because of user interaction.
     * i.e. user clicked on tab in sidebar
     * @param tabName to open / navigate to
     */
    onSidebarTabChanged: (tabName: string) => {
      const state = get();
      const prevNavigableTab = getSideBarPrevNavigableTab(state);
      const prevFolderId = getFolderForTab(state, prevNavigableTab);
      const nextFolderId = getFolderForTab(state, tabName);

      const navigate = isFolderNavigableTab(state, tabName) && nextFolderId !== prevFolderId;

      set(
        produce<PortalStore>(state => {
          state.sideBar.activeTab = tabName;
          state.sideBar.isOpen = true; // user clicked a tab. Open the sidebar
          if (navigate) {
            // track the previous navigable tab. not all tab changes will trigger folder navigation.
            state.sideBar.prevNavigableTab = tabName;
          }
        })
      );

      if (navigate) {
        state.actions.navigateToFolder(nextFolderId);
      }
    },

    setSidebarOpen: (open: boolean) =>
      set(
        produce<PortalStore>(state => {
          state.sideBar.isOpen = open;
        })
      ),
    toggleSidebarOpen: () => {
      // because sidebarOpen is a calculated value, we need to use the selector for it everywhere
      const isOpen = getSideBarOpen(get());
      set(
        produce<PortalStore>(state => {
          state.sideBar.isOpen = !isOpen;
        })
      );
    },

    setPortalContentScroll: (x: number, y: number) => set({ portalContentScroll: { x, y } }),

    /**
     *
     * @param folderId Mock for tests
     */
    setCurrentFolderId: (folderId: string) => {
      const activeTab = getSideBarActiveTab(get());
      set(
        produce<PortalStore>(state => {
          state.navigation.sidebarTabs[activeTab] = { activeFolder: folderId };
        })
      );
    },

    /** Show empty page contents... */
    showEmptyPage: (arePageContentsEmpty?: boolean) => {
      // this happens as a result of navigation, and does not update navigation the way the legacy one did.
      if (getArePageContentsEmpty(get()) !== arePageContentsEmpty) {
        set({ arePageContentsEmpty });
        if (arePageContentsEmpty) {
          LoadingStore.getState().actions.doneLoadingGlobally(getNavigationBusyId());
          set({ isInitialized: true });
        }
      }
    },

    // would this be actually different for folder-tree versus breadcrumbs?
    /**
     * Update folder id and page name and fetch page data, etc.
     * This is now meant to be called indirectly, by navigateToFolder updating the browser URL.
     * Calling this directly could cause UI and URL to get out of sync.
     * @param folderId to navigate to
     * @param pageName to render
     * @param force `true` to reload page, etc. even if folder & page id match what's in the store.
     */
    onFolderNavigation: async (folderId: string, pageName?: string, force = false) => {
      const { actions } = get();
      const currentFolderId = getFolderId(get());
      let folderStoreId = getCurrentFolderId(FolderStore.getState());
      let activePageName = getActivePageName(FolderStore.getState());
      const shouldReplacePageName = !pageName;

      // read navigation parameters out of the URL, here
      const newNavParams = getAdditionalParameters();
      const navParamsChanged = newNavParams !== get().navigation.navigationParams;
      let busyId = getNavigationBusyId(folderId, pageName, newNavParams);
      const stillNavigatingHere = getGlobalLoadingState(LoadingStore.getState()).get(busyId);

      if (
        stillNavigatingHere ||
        (folderId === currentFolderId &&
          pageName === activePageName &&
          !force &&
          !shouldReplacePageName &&
          // trying to think of a scenario where the history.location.search could change, but the page, folder, and
          // other nav params not change. If it doesn't exist, we don't need this check.
          !navParamsChanged)
      ) {
        // probably don't need to clear any busy state here, as navigation could be ongoing async when this is called.
        // actions.doneLoadingGlobally(busyId);
        return; // we're already there, don't do anything.
      }
      // update folderId immediately to avoid duplicate API calls
      actions.selectFolderForTabs(folderId, state => {
        // update nav params, so we know if they have changed next time
        state.navigation.navigationParams = newNavParams;
      });

      LoadingStore.getState().actions.startLoadingGlobally(busyId);

      if (!IS_TEST && !DEV_SERVER) {
        // hide any straggling legacy jQuery menus:
        Decisions.ActionHelper.HideMenu({ which: 3 });
      }

      if (folderId !== folderStoreId) {
        // unsubscribe / resubscribe to runtimeFilterChanges
        updateRuntimeFilterSubscriptions(folderId, currentFolderId);

        // update folder store / make sure folderViewData, etc. is loaded.
        const folderViewData = await FolderStore.getState().folderStoreActions.goToFolder(folderId, pageName);
        if (!folderViewData) {
          return actions.navigateToFolder(getDefaultFolderId(SettingsStore.getState()));
        }

        actions.loadAndSelectNode(folderId);

        // if pageName was NOT passed in, we need to make sure the page content we ask for is what's
        // in the folder FolderStore for it (likely the default):
        pageName = pageName ? pageName : getActivePageName(FolderStore.getState());
      } else {
        // just update page:
        pageName = pageName || getCurrentFolderDefaultPageName(FolderStore.getState());
        // prettier chokes on ??=... maybe it's time for an upgrade.
        FolderStore.getState().folderStoreActions.setActivePage(pageName);
      }

      if (shouldReplacePageName && pageName) {
        // get updated query param to include page name
        const updatedQuery = getUpdatedSearchParams({ FolderId: folderId, pageName });
        // update busy tracking to include the page name:
        const busyIdWithPage = getNavigationBusyId(folderId, pageName, newNavParams);
        LoadingStore.getState().actions.updateBusyId(busyIdWithPage, busyId);
        busyId = busyIdWithPage;
        // replace current history entry, instead of adding a new one. This will trigger this function to be invoked
        // again, but with identical details, it will return early via the guard clause.

        history.replace({
          pathname: history.location.pathname,
          search: updatedQuery,
        });
      }

      if (DEV_SERVER) {
        // we can't render pages without $D and $DP constructs, yet, so in dev server, we're just rendering
        // the Christmas goose.
        actions.setPageData(
          `<div style="padding: 1rem;">
            <h3 id="dev-server-test-page-title" >Scripts Did Not Evaluate</h3>
            <section style="min-width: 1000px; height: 1000px; border: solid 1px gray;">
              <img src="https://ychef.files.bbci.co.uk/1600x900/p0bbdyj7.webp" style="width: 100%;">
            </section>
            <script>
              document.getElementById('dev-server-test-page-title').innerText = "YAY Scripts Evaluated!";
            </script>
          </div>`
        );
        return;
      }

      let navigationParameters = getNavigationParams(newNavParams, true);
      // Should page be re-fetched if only the navigationParams changed?
      // Maybe not in future, but this is how 7 works, and AUTs fail if we don't refetch in that scenario.
      const response = await fetchWithParameters({
        url: '/Folder/',
        urlParams: {
          id: folderId,
          pageName,
          ...navigationParameters.object,
        },
      });
      const pageData = await response.text();

      const showEmpty = !pageData || response.ok === false;
      // show some alternate page content if something goes wrong here...
      actions.showEmptyPage(showEmpty);

      showEmpty || actions.setPageData(pageData);
      window.dispatchEvent(createEvent('folderChangedEvent', folderId));

      syncClientsEventAndFoldersListToServer();

      // TODO migrate stuff inside sendNavCompleteMessages, so we no longer need this:
      !IS_TEST && !DEV_SERVER && Decisions.Navigation.sendNavCompleteMessages(folderId, pageName);

      // notify server, and raise events if nav parameters have changed
      raiseNavParamUpdateEvent(navigationParameters);

      LoadingStore.getState().actions.doneLoadingGlobally(busyId);
      set({ isInitialized: true });
    },
    /**
     * Call this to trigger navigation to a folder or page. This method is responsible for updating the URL,
     * then the history change subscription (and in future, route components) will trigger
     * the appropriate navigation handler, e.g. `onFolderNavigation`
     * @param folderId id to navigate to
     * @param pageName page name to navigate to
     * @param navigationParameters any additional parameters besides core navigation parameters.
     * @param retainParameters by default, all additional params are lost when switching between folders.
     * to maintain them, add `retainparams: true` to navigationParameters, or set this argument to `true`.
     * @returns `true` if navigation needed to happen. A very few things, like some forms.navigateInSubWindow need to know.
     */
    navigateToFolder: (folderId: string, pageName?: string, navigationParameters = {}, retainParameters?: boolean) => {
      if (!folderId) {
        return false;
      }

      //clearing the mvc mavigation params
      !IS_TEST && $DP.Navigation.Helper.clearNavigationParameters();

      // when there are too many subfolders, a [Search Folders] appears in the tree, instead. Nav is to it's parent.
      folderId = folderId.replace('.SearchFolder', '');
      const initialized = getInitialized(get());
      const baseUrl = getBaseUrl(get());

      // nav params args only used by form, tile, and action handlers
      navigationParameters = evaluateNavParams(folderId, navigationParameters, retainParameters);

      // get updated query params from folderId pageName, new params, and conditionally, the current URL
      const updatedQuery = getUpdatedSearchParams({ FolderId: folderId, pageName, ...navigationParameters });

      if (updatedQuery?.replace('?', '') === history.location.search?.replace('?', '')) {
        return false; // we didn't need to navigate, since the resulting URL would be identical
      }

      // if the portal isn't initialized, e.g. we are viewing some specific UI, like /EntityDetails/
      // we'll need to navigate to the actual portal if this is called, instead of pushing to history
      if (!initialized) {
        // when this isn't initialized, the `portalBaseUrl` global should still have the StudioH or H part
        window.location.href = history.createHref({ pathname: getBasePath(baseUrl), search: updatedQuery });
        return;
      }

      // TODO if _only_ navParameters have changed, then that should be a replace, probably, not a push.
      // Should that just be a different method?

      // navigateToFolder is always a push, not a replace,
      // ...unless we want something like runtime filters to only replace?
      // just update the URL, and onFolderNavigation should handle the other changes
      history.push({
        pathname: history.location.pathname,
        search: updatedQuery,
      });

      // Decisions.Navigation.navigateToFolder now calls this.
      return true;
    },

    /**
     * If you have the folder path to navigate to.
     */
    navigateToFolderByPath: async (folderPath: string, pageName?: string) => {
      const folder = await get().actions.getFolderByPath(folderPath);
      get().actions.navigateToFolder(folder.FolderID, pageName);
    },

    /**
     * If the page name updates while the user is on that page, we need to replace that piece of history.
     * This can happen before FolderStore state update.
     */
    updatePageName(pageName: string) {
      // pass replace:false to preserve all the other arguments besides pageName:
      const updatedQuery = getUpdatedSearchParams({ pageName }, true);
      history.replace({
        pathname: history.location.pathname,
        search: updatedQuery,
      });
    },

    showFolderInLayer: (folderId: string) => {
      // this piece of state was moved to Router.tsx to avoid circular imports, because nothing is subscribed to it.
      showFolderInLayer(folderId);
    },

    closeFolderLayer: (folderId: string) => {
      closeFolderLayer(folderId);
    },

    /**
     * Make sure the folder is "active" for every tab that has that folder
     * @param folderId to select
     * @param otherUpdates combine other state updates that should happen at the same time (avoid one update cycle)
     */
    selectFolderForTabs: (folderId: string, otherUpdates?: (state: WritableDraft<PortalStore>) => void) => {
      // TODO should only need to do this
      const { actions } = get();
      let treesContainingFolder = actions
        .getTreesContainingFolder(folderId)
        .map(tree => getTabNameForTreeId(get(), tree?.treeId))
        .filter(id => id);

      // if it's not found anywhere, it's a new folder, and the trees are just not up to date, yet.
      // Folders are almost always added while on the Folders tab, so it could be save to assume either Folders or current.
      treesContainingFolder = treesContainingFolder.length ? treesContainingFolder : [getSideBarActiveTab(get())];

      set(
        produce<PortalStore>(state => {
          // set activeFolder for any tabs containing that folder:
          treesContainingFolder.forEach(tab => (state.navigation.sidebarTabs[tab] = { activeFolder: folderId }));
          // always update recent and favorites, because their selections should always reflect changes on any other tab:
          state.navigation.sidebarTabs[getRecentFoldersPanelTitle(get())] = { activeFolder: folderId };
          state.navigation.sidebarTabs[getFavoritesPanelTitle(get())] = { activeFolder: folderId };
          // any other necessary state updates to combine with this "set" call.
          otherUpdates?.(state);
        })
      );
    },

    setPageData: (data: string) => {
      const { navigation } = get();
      if (navigation.pageHost) {
        // if we have the page element, just render the page, don't store the data
        renderPageContents(navigation.pageHost, data);
      } else {
        // if we have the data, but we don't have the page element, yet, store the data
        set(
          produce<PortalStore>(state => {
            state.navigation.currentPageData = data;
          })
        );
      }
    },

    /**
     * We could have a debounced listener on the actual element to deal with animations, but
     * since we have no idea how complex customer pages will be and therefore what the performance impact
     * would be during animations such as sidebar open/close, we'll just imperatively do this once when
     * we know we need to.
     */
    onPageAreaSizeChange: () => {
      const contentElement = document.getElementsByClassName('dp-portal-frame__page-contents')[0] as HTMLElement;
      if (!contentElement) {
        return;
      }

      // not using this for anything, yet, but we'll probably need it later.
      set(
        produce<PortalStore>(state => {
          state.pageContentSize.width = contentElement.offsetWidth;
          state.pageContentSize.height = contentElement.offsetHeight;
        })
      );

      // trigger re-measuring the page size / re-layout of size dependent things
      if (!DEV_SERVER && !IS_TEST) {
        // for now by telling web.core code about it.
        $DP.Portal.dispatchPortalResize();
      }
    },

    refreshCurrentPage: () => {
      const { actions } = get();
      const activePageName = getActivePageName(FolderStore.getState());
      const currentFolderId = getFolderId(get());

      actions.onFolderNavigation(currentFolderId, activePageName, true);
    },

    onSidePanelResize: (position: ISplitterDelta) =>
      set(
        produce<PortalStore>(state => {
          state.sideBar.width += position.deltaX;
        })
      ),
    onPortalResize: () => {
      set({
        width: document.body.offsetWidth,
        height: document.body.offsetHeight,
      });
    },

    restartPortal: () => {
      // TODO a bunch of async state updates, probably, and maybe re-rendering some UI.
    },

    openPortal: () => {
      openPortal();
    },

    setInitializing: (isInitializing?: boolean) => {
      set({ isInitialized: !isInitializing });
    },

    showImpersonationDialog() {
      // TODO implement ImpersonationDialog in React later:
      $DP.Dialogs.ImpersonationDialog.ShowImpersonateDialog();
    },

    switchPortal() {
      const { baseUrl, isStudio } = get();
      switchPortal(baseUrl || '', !isStudio);
    },

    toggleSystemInformation: () => {
      // TODO render system information directly in React
      set(
        produce<PortalStore>(state => {
          state.navigation.showingSystemInformation = !state.navigation.showingSystemInformation;
        })
      );
      SystemInformation();
    },

    toggleSubmitFeedback: () => {
      // TODO render User Feedback dialog in React
      // set(
      //   produce<PortalStore>(state => {
      //     state.navigation.showingSystemInformation = !state.navigation.showingSystemInformation;
      //   })
      // );
      Userback.open?.();
    },

    /**
     * logout is a whole portal UI concern, not just an account concern, so it's here.
     */
    logOut: () => AccountsLogout(getReturnUrl(get())),

    /**
     * Logout, but show login screen with reference to current user.
     */
    lockPortal: () => AccountsLogout(window.location.href, getCurrentUserEmail(SettingsStore.getState())),

    addRootFolder: async () => {
      const state = get();
      const currentFolderId = getFolderId(state);
      if (!currentFolderId) {
        return;
      }

      await executeActionByClientActionId(
        'CreateFolderActionId',
        'DecisionsFramework.ServiceLayer.Services.Folder.Folder',
        currentFolderId
      );
    },

    initializeHistory(loadModel: PortalLoadModel) {
      // set up subscription
      history.listen(({ action, location }) => {
        // on URL change we need to respond, regardless of where the change came from. URL is source of truth.
        const { folderId, pageName } = getLocationPartsFromUrl(location);
        if (!folderId) {
          return;
        }
        // if folder, page, and nav params are the same, onFolderNavigation will be a NOOP
        // onFolderNavigation will read additionalParams out of the URL
        get().actions.onFolderNavigation(folderId, pageName, false);
      });

      document.addEventListener('navigationParameterChanged', () => {
        const additionalParams = getAdditionalParameters(window.location.search);
        if (additionalParams) {
          set({
            navigation: {
              ...get().navigation,
              ...{
                navigationParams: additionalParams,
              },
            },
          });
        }
      });

      // make sure initial URL is correct, meaning if we don't have a folderId in the URL, or it doesn't match the
      // loadModel, grab from the loadModel and replace that entry in the history:
      const { folderId, pageName } = getLocationPartsFromUrl(history.location);
      if (!folderId || folderId !== loadModel.CurrentFolderView.FolderID || pageName !== loadModel.CurrentPageName) {
        // preserve any other params we get on initial portal load:
        const additionalParams = getNavigationParams(history.location.search).object;
        // replace current history entry, instead of adding a new one, when we are initializing:
        history.replace({
          pathname: history.location.pathname,
          search: getUpdatedSearchParams({
            FolderId: loadModel.CurrentFolderView.FolderID,
            pageName: loadModel.CurrentPageName,
            ...additionalParams,
          }),
        });
      } else {
        get().actions.onFolderNavigation(folderId, pageName, false);
      }
    },

    ...createFavoriteActions(get, set),

    ...createHelpCenterGlobalActions(get, set),

    ...createFoldersStoreActions(get, set),
  };
}

function createPortalAccountActions(get: GetState<PortalStore>, set: SetState<PortalStore>) {
  return {
    impersonateUser: (userName: string) => {
      const impersonations = get().accountInfo.accountImpersonations;
      const identifier = impersonations.find(imp => (imp.Name = userName));
      if (identifier == null) {
        Decisions.MessageDialog.Info(`You don't have permissions to impersonate as ${userName}`, 'Impersonate User');
      } else if (userName != null) {
        get().accountActions.impersonateAccount(identifier.Id);
      }
    },

    impersonateAccount: async (accountId: string) => {
      const { actions } = get();
      actions.setInitializing(true);
      let impersonated = false;
      try {
        // currently handled in home controller not service layer :grimacing:
        const response = await fetch(virtualPath + '/home/ImpersonateAccount', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'json',
          },
          body: JSON.stringify({ accountId }),
        });
        const data = await response.json();
        impersonated = data === 'True' || data === true;
      } catch (error) {
        console.error('Error requesting impersionation', error);
      }

      actions.setInitializing(false);
      if (impersonated) {
        actions.restartPortal();
      } else {
        // oof do we have a FrontEnd way to do this?
        $DP.Dialogs.ErrorPopup.showError(
          'Unable to Impersonate',
          'Unable to Impersonate. Please check if the User you are trying to Impersonate as is still active.',
          'Impersonation Action Failed'
        );
      }
    },

    cancelImpersonation: () => {
      // TODO
    },

    fetchAccount: async () => {
      try {
        const account = await getCurrentAccount({ userContext: ServiceFetcher.getUserContext() });
        set(
          produce<PortalStore>(state => {
            state.accountInfo.account = account;
          })
        );
      } catch (error) {
        console.error('Error loading account');
      }
    },
  };
}

//#endregion

//#region selector functions

// selector functions could just as easily be inline, but having them pre-defined can DRY up
// reuse, and make them easier to test if they had a complex calculation.
// They also shield components from the effects of store-object refactors.

export function getIsDebugEnabled(state: PortalStore) {
  return state.isDebuggingEnabled;
}

export function getIsMaintenanceModeEnabled(state: PortalStore) {
  return state.isMaintenanceModeEnabled;
}

export function getShowGlobalDebuggerIndicator(state: PortalStore) {
  return state.isStudio && state.showGlobalDebuggerIndicator;
}

export function getSideBarActiveTab(state: PortalStore) {
  return state.sideBar.activeTab || getDefaultActiveTabName(state);
}

export function isActiveSidebarTabWorkflows(state: PortalStore) {
  return getSideBarActiveTab(state) === getWorkflowCatalogPanelTitle(state);
}
export function isActiveSidebarTabFolders(state: PortalStore) {
  return getSideBarActiveTab(state) === getFoldersPanelTitle(state);
}
export function isActiveSidebarTabRecent(state: PortalStore) {
  return getSideBarActiveTab(state) === getRecentFoldersPanelTitle(state);
}
export function isActiveSidebarTabSystem(state: PortalStore) {
  // system can't be active if it's not visible
  return getSystemFolderVisible(state) && getSideBarActiveTab(state) === getSystemFoldersPanelTitle(state);
}

export function getSideBarPrevNavigableTab(state: PortalStore) {
  return state.sideBar.prevNavigableTab || isFolderNavigableTab(state, state.sideBar.activeTab)
    ? state.sideBar.activeTab
    : '';
}

export function getSideBarWidth(state: PortalStore) {
  return getIsDesktopSize(state) ? state.sideBar.width : DEFAULT_SIDEBAR_WIDTH;
}

/**
 * @param state snapshot to evaluate
 * @returns whether sidebar is open will be treated as open based on portal settings, then frameStyle (chrome), then user interaction thereafter.
 */
export function getSideBarOpen(state: PortalStore) {
  const settings = SettingsStore.getState();
  const frameStyle = getFrameStyle(settings);
  const isStudio = getIsStudio(state);
  const open =
    state.sideBar.isOpen !== undefined
      ? state.sideBar.isOpen
      : frameStyle === ChromeSetting.NoTopBarFolderTreeCollapsed ||
        frameStyle === ChromeSetting.TopBarFolderTreeCollapsed ||
        (isStudio ? getCollapsedTreeOnStartStudio(settings) : getCollapsedTreeOnStartUserPortal(settings))
      ? false
      : true;
  return open;
}

export function getActions(state: PortalStore) {
  return state.actions;
}

export function getAccountActions(state: PortalStore) {
  return state.accountActions;
}

export function getSearchProps(state: PortalStore) {
  return state.search;
}

export function getHideTopBarSearch(state: PortalStore) {
  const isPortal = getIsStudio(state) === false;
  return isPortal && SettingsStore.getState().portalSettings.Settings.HideTopBarSearch;
}

export function getInitialized(state: PortalStore) {
  return state.isInitialized;
}

export function getFolderId(state: PortalStore) {
  return getFolderForActiveTab(state);
}

export function getFolderForActiveTab(state: PortalStore) {
  const tab = getSideBarActiveTab(state);
  return getFolderForTab(state, tab);
}

export function getPageData(state: PortalStore) {
  return state.navigation.currentPageData;
}

export function getArePageContentsEmpty(state: PortalStore) {
  return state.arePageContentsEmpty;
}

export function getIsStudio(state: PortalStore) {
  return state.isStudio;
}

export function getBaseUrl(state: PortalStore) {
  return state.baseUrl;
}

export function getReturnUrl(state: PortalStore) {
  return state.returnUrl || '';
}

export function getFoldersPanelTitle(state: PortalStore) {
  return state.foldersPanelTitle;
}
export function getFavoritesPanelTitle(state: PortalStore) {
  return state.favoritesPanelTitle || '';
}

export function getSystemFoldersPanelTitle(state: PortalStore) {
  return state.systemFoldersPanelTitle as string;
}
export function getSystemFolderVisible(state: PortalStore) {
  return state.systemFolderVisible;
}

export function getRecentFoldersPanelTitle(state: PortalStore) {
  return state.recentFoldersPanelTitle as string;
}

export function getWorkflowCatalogPanelTitle(state: PortalStore) {
  return state.workflowCatalogPanelTitle;
}

export function getWorkflowCatalogPanelVisibility(state: PortalStore) {
  return !!(state.workflowCatalogRootNodes?.length || state.isStudio);
}

export function getWorkflowCatalogPanelRootNodes(state: PortalStore) {
  return state.workflowCatalogRootNodes;
}

export function getPortalWidth(state: PortalStore) {
  return state.width;
}

export function getIsDesktopSize(state: PortalStore) {
  return state.width > PORTAL_BREAKPOINT_TABLET;
}

export function getIsTabletSize(state: PortalStore) {
  return state.width <= PORTAL_BREAKPOINT_TABLET;
}

export function getPortalHeight(state: PortalStore) {
  return state.height;
}

export function getIsImpersonating(state: PortalStore) {
  return state.accountInfo.isImpersonating;
}

export function getAccountImpersonations(state: PortalStore) {
  return state.accountInfo.accountImpersonations;
}

export function getIsDesignOrAdminUser(state: PortalStore) {
  return state.accountInfo.isUserInDesignersOrAdministratorGroup;
}

export function getIsAdminUser(state: PortalStore) {
  return state.accountInfo.isAdministratorUser;
}

export function getCanSubmitFeedback(state: PortalStore) {
  return state.accountInfo.canSubmitFeedback && typeof Userback !== 'undefined' && typeof Userback.open !== 'undefined';
}

// having x and Y as separate, primitive selectors should avoid extra re-renders when one changes, but not the other.
export function getPortalContentScrollY(state: PortalStore) {
  return state.portalContentScroll.y;
}

export function getPortalContentScrollX(state: PortalStore) {
  return state.portalContentScroll.x;
}

export function getSidebarContentSelector(state: PortalStore) {
  return state.sideBar.sidebarContentSelector;
}

export function getDefaultActiveTabName(state: PortalStore) {
  const defaultActiveTab = getDefaultActiveTab(SettingsStore.getState());
  switch (defaultActiveTab) {
    case PortalSideBarDefaultPanel.Recent:
      return getRecentFoldersPanelTitle(state);
    case PortalSideBarDefaultPanel.WorkflowCatalog:
      return getWorkflowCatalogPanelTitle(state);
    default:
      return getFoldersPanelTitle(state);
  }
}

export function getShouldNotChangeTabFromFolderTree(state: PortalStore) {
  // need to preserve workflow during initialization, to make "Panel Opened On Start-up" portal setting work.
  return !state.isInitialized && state.sideBar.activeTab === getWorkflowCatalogPanelTitle(state);
}

export function getFolderSet(state: PortalStore) {
  return state.areFoldersSet;
}

//#endregion

//#region hooks
// hooks help DRY up boilerplate around getting certain references, e.g.

/**
 * Example of a selector hook that uses local state and memoizes with useCallback
 * @param name some local state you need to use with shared state to pick / derive a value
 * @returns that unique value
 */
export function useSideBarTabIsActive(name: string) {
  return usePortalStore(useCallback(state => getSideBarActiveTab(state) === name, [name]));
}

/**
 * A hook because pageSize is derived / stored in settings.
 * @returns portal search props object.
 */
export function useGetPortalSearchProps(): IPortalSearchBoxProps {
  const props = usePortalStore(getSearchProps);
  const pageSize = SettingsStore(getSearchPageSize);
  return useMemo(
    () => ({
      ...props,
      pageSize,
    }),
    [pageSize, props]
  );
}

export function useIsActiveFolder(folderId: string) {
  return usePortalStore(useCallback(state => getFolderId(state) === folderId, [folderId]));
}

//#endregion hooks

//#region global handlers

window.addEventListener('resize', () => {
  // TODO do we need debounce or RAF here, or is already throttled?
  usePortalStore.getState().actions.onPortalResize();
});

//#endregion

//#region helpers

/**
 * Render into target element using vanilla JS to escape any issues with useEffect hooks,
 * and be able to manage whether page data (which can be megs in some cases) lives in the store or not.
 *
 * This to avoid race conditions introduced between Navigation.ts#updatePage, portal.ts#initialize and React rendering the portal HTML.
 *
 * @param el page content target
 * @param data HTML data string
 */
function renderPageContents(el: HTMLElement, data: string) {
  // page rendering is still in MVC.
  if (!DEV_SERVER && !IS_TEST) {
    // this has to happen to unplug all the controls before removing the HTML from this content element:
    $DP.Controls.ControlDisposeHelper.CallDisposeBeforeDeletingElementFromDom(el, false);
    // have to use jQuery to remove things in order to trigger jQuery.on('remove') events, for now.
    (window as any).$(el).empty();
    // still using jQuery.append for now, since something about script evaluation/execution order is
    // different there than in our little utility, which is causing race conditions, e.g. charts trying
    // to render before the chart bundle is loaded. Maybe it's as simple as globbing them all together,
    // but the path of least risk for 8.0 is just to fall back on jQuery.append.
    (window as any).$(el).append(data);
    // initializeClientContainers has to happen before doLayout
    $DP.Containers.Utils.initializeClientContainers(el);
    $DP.Containers.PageRootContainer.doLayout();
    $Portal.Progress.hideLoadingIndicator();
    // $DP.UI.Helper.Resize();
    document.dispatchEvent(new Event('decisionsnavigationcomplete'));
    window.setTimeout(() => $DP.Controls.ControlDisposeHelper.CallForceDispose(), 0);
  } else {
    // empty it out:
    el.innerHTML = '';
    // this is okay for dev server
    el.innerHTML = data;
    executeScriptElements(el);
  }
}

async function raiseNavParamUpdateEvent(params: ReturnType<typeof getNavigationParams>) {
  if (IS_TEST || DEV_SERVER) {
    return;
  }

  // leaving this in, until we really convert all these, in [DT-035640]
  await $DP.Navigation.Helper.UpdateNavigationParameters(params.object, true);

  // TODO migrate pageContextId and this event raising business:
  // AFAICT the _should_ work but doesn't. One difference is standard things urlEncode the search params, but $DP.Navigation.Helper does not.
  // await setPageParameters({
  //   pageContextId: Decisions.Navigation.pageContextId,
  //   parms: params.dataPairs,
  // });

  // // still need to update this legacy helper for now, as it's referenced _all over the place_.
  // $DP.Navigation.Helper.navigationParameters = params.object;
  // $DP.Navigation.Helper.raiseEvent();
}

function getFolderForTab(state: PortalStore, tab: string) {
  return state.navigation.sidebarTabs[tab]?.activeFolder ?? getDefaultFolderId(SettingsStore.getState()) ?? '';
}

// TODO might not need this
function isFolderNavigableTab(state: PortalStore, tabName: string) {
  switch (tabName) {
    case getFoldersPanelTitle(state):
    case getSystemFoldersPanelTitle(state):
    case getRecentFoldersPanelTitle(state):
      return true;
    default:
      return false;
  }
}

function getTabNameForTreeId(state: PortalStore, treeId?: string) {
  return treeId ?? '';
}

function getNavigationBusyId(folderId?: string, pageName?: string, params?: string) {
  // URL should work for this:
  if (!folderId || !pageName) {
    // logical or to avoid parsing these if we don't need to
    const currentFolderAndPage = getLocationPartsFromUrl();
    folderId = folderId || currentFolderAndPage.folderId;
    pageName = pageName === undefined ? currentFolderAndPage.pageName : '';
  }
  params = params || getAdditionalParameters();
  const ids = [folderId, pageName, params].filter(id => !!id).join('_');

  return `Navigating_${ids}`;
}

//#endregion helpers
